import { useState, useCallback } from 'react';
import { api } from '@expat-ui/shared';
import { getFreshAccessToken } from '../../utils/authUtils';
import {
    ProjectionResult,
    Country,
    Currency,
    GoalFundingSummary,
    GOAL_CATEGORY_LABELS,
    SavedScenario,
} from '../../types/api.types';
import { formatAmount } from '../../utils/numberFormat';
import styles from './SimulatorPage.module.scss';

const RETIREMENT_LOCATIONS: { value: Country; label: string }[] = [
    { value: 'INDIA', label: 'India (INR)' },
    { value: 'US', label: 'United States (USD)' },
    { value: 'CAN', label: 'Canada (CAD)' },
];

const SWR_OPTIONS = [3, 3.5, 4, 4.5, 5];

const STORAGE_KEY = 'expat-scenarios';

function getSavedScenarios(): SavedScenario[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function saveScenario(scenario: SavedScenario) {
    const scenarios = getSavedScenarios();
    if (scenarios.length >= 5) {
        scenarios.shift();
    }
    scenarios.push(scenario);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
}

function deleteScenario(id: string) {
    const scenarios = getSavedScenarios().filter((s) => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
}

export function SimulatorPage() {
    const [retirementAge, setRetirementAge] = useState(55);
    const [retirementCountry, setRetirementCountry] = useState<Country>('INDIA');
    const [withdrawalAmount, setWithdrawalAmount] = useState<number | undefined>(undefined);
    const [selectedSWR, setSelectedSWR] = useState(4);
    const [result, setResult] = useState<ProjectionResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>(getSavedScenarios());
    const [scenarioName, setScenarioName] = useState('');
    const [showSavePrompt, setShowSavePrompt] = useState(false);

    const runSimulation = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getFreshAccessToken();
            if (!token) throw new Error('Not authenticated');
            const data = await api.post<ProjectionResult>(
                '/projection',
                {
                    retirementAge,
                    retirementCountry,
                    ...(withdrawalAmount ? { withdrawalAmount } : {}),
                },
                token,
            );
            setResult(data);
            setShowSavePrompt(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Simulation failed');
        } finally {
            setLoading(false);
        }
    }, [retirementAge, retirementCountry, withdrawalAmount]);

    const handleSaveScenario = () => {
        if (!scenarioName.trim() || !result) return;
        const scenario: SavedScenario = {
            id: Date.now().toString(),
            name: scenarioName,
            savedAt: new Date().toISOString(),
            inputs: {
                retirementAge,
                retirementCountry,
                withdrawalAmount,
            },
            results: {
                successRate: result.successRate,
                safeAnnualSpend: result.safeAnnualSpend,
                startingWealth: result.startingWealth,
                retirementAge: result.retirementAge,
                baseCurrency: result.baseCurrency,
            },
        };
        saveScenario(scenario);
        setSavedScenarios(getSavedScenarios());
        setScenarioName('');
        setShowSavePrompt(false);
    };

    const formatCurrency = (amount: number, currency: string) => {
        return formatAmount(Math.round(amount), currency as Currency);
    };

    const fireNumberAtSWR = result ? result.safeAnnualSpend * (100 / selectedSWR) : 0;

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Retirement Simulator</h1>
                <p className={styles.subtitle}>
                    Adjust levers below and run a Monte Carlo simulation to project your retirement readiness.
                </p>
            </div>

            <div className={styles.controlPanel}>
                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>
                        Retirement Age
                        <span className={styles.sliderValue}>{retirementAge}</span>
                    </label>
                    <input
                        type="range"
                        className={styles.slider}
                        min={40}
                        max={70}
                        value={retirementAge}
                        onChange={(e) => setRetirementAge(Number(e.target.value))}
                    />
                    <div className={styles.sliderRange}>
                        <span>40</span>
                        <span>70</span>
                    </div>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>Primary Retirement Location</label>
                    <select
                        className={styles.select}
                        value={retirementCountry}
                        onChange={(e) => setRetirementCountry(e.target.value as Country)}
                    >
                        {RETIREMENT_LOCATIONS.map((loc) => (
                            <option key={loc.value} value={loc.value}>
                                {loc.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.sliderGroup}>
                    <label className={styles.sliderLabel}>
                        Annual Withdrawal (optional)
                        <span className={styles.sliderValue}>
                            {withdrawalAmount ? formatCurrency(withdrawalAmount, 'INR') : 'Auto'}
                        </span>
                    </label>
                    <input
                        type="range"
                        className={styles.slider}
                        min={0}
                        max={20000000}
                        step={100000}
                        value={withdrawalAmount || 0}
                        onChange={(e) => {
                            const val = Number(e.target.value);
                            setWithdrawalAmount(val > 0 ? val : undefined);
                        }}
                    />
                    <div className={styles.sliderRange}>
                        <span>Auto (4%)</span>
                        <span>2 Cr</span>
                    </div>
                </div>

                <button className={styles.runButton} onClick={runSimulation} disabled={loading}>
                    {loading ? 'Running Simulation...' : 'Run Simulation'}
                </button>
            </div>

            {error && <p className={styles.error}>{error}</p>}

            {result && (
                <div className={styles.results}>
                    <div className={styles.verifiedBadge}>
                        <span className={styles.verifiedIcon}>✓</span>
                        Monte Carlo Verified — 1,000 simulations
                    </div>

                    <div className={styles.metricsRow}>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>SUCCESS PROBABILITY</span>
                            <span
                                className={`${styles.metricValue} ${result.successRate >= 80 ? styles.good : result.successRate >= 60 ? styles.moderate : styles.poor}`}
                            >
                                {result.successRate}%
                            </span>
                            <span className={styles.metricStatus}>
                                {result.successRate >= 80
                                    ? 'Stable'
                                    : result.successRate >= 60
                                      ? 'Moderate Risk'
                                      : 'High Risk'}
                            </span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>SAFE ANNUAL SPEND</span>
                            <span className={styles.metricValue}>
                                {formatCurrency(result.safeAnnualSpend, result.baseCurrency)}
                            </span>
                            <span className={styles.metricStatus}>{result.baseCurrency}/Year (inflation-adjusted)</span>
                        </div>
                        <div className={styles.metricCard}>
                            <span className={styles.metricLabel}>STARTING NET WORTH</span>
                            <span className={styles.metricValue}>
                                {formatCurrency(result.startingWealth, result.baseCurrency)}
                            </span>
                            <span className={styles.metricStatus}>Across all accounts</span>
                        </div>
                    </div>

                    <div className={styles.swrSection}>
                        <div className={styles.swrHeader}>
                            <span className={styles.swrLabel}>Safe Withdrawal Rate Playground</span>
                        </div>
                        <div className={styles.swrButtons}>
                            {SWR_OPTIONS.map((swr) => (
                                <button
                                    key={swr}
                                    className={`${styles.swrButton} ${selectedSWR === swr ? styles.active : ''}`}
                                    onClick={() => setSelectedSWR(swr)}
                                >
                                    {swr}%
                                </button>
                            ))}
                        </div>
                        {fireNumberAtSWR > 0 && (
                            <div className={styles.swrInfo}>
                                At {selectedSWR}% SWR, you need {formatCurrency(fireNumberAtSWR, result.baseCurrency)}{' '}
                                to retire safely
                            </div>
                        )}
                    </div>

                    <div className={styles.projectionChart}>
                        <h3 className={styles.chartTitle}>Projected Net Worth Timeline</h3>
                        <div className={styles.chartContainer}>
                            {result.yearlyDataPoints.map((dp) => {
                                const totalNW =
                                    dp.netWorthByCountry.india + dp.netWorthByCountry.us + dp.netWorthByCountry.can;
                                const maxNW = Math.max(
                                    ...result.yearlyDataPoints.map(
                                        (p) =>
                                            p.netWorthByCountry.india +
                                            p.netWorthByCountry.us +
                                            p.netWorthByCountry.can,
                                    ),
                                );
                                const height = maxNW > 0 ? (totalNW / maxNW) * 100 : 0;
                                const isRetirement = dp.age === result.retirementAge;
                                const hasGoal = (dp.goalEvents?.length ?? 0) > 0;
                                const goalTitles = dp.goalEvents?.map((g) => g.label).join(', ');
                                return (
                                    <div
                                        key={dp.year}
                                        className={[
                                            styles.bar,
                                            dp.isShortfallDetected ? styles.shortfall : '',
                                            isRetirement ? styles.retirement : '',
                                            hasGoal ? styles.goalMarker : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        style={{ height: `${Math.max(2, height)}%` }}
                                        title={
                                            hasGoal
                                                ? `Age ${dp.age}: ${formatCurrency(totalNW, result.baseCurrency)} — Goal: ${goalTitles}`
                                                : `Age ${dp.age}: ${formatCurrency(totalNW, result.baseCurrency)}`
                                        }
                                    />
                                );
                            })}
                        </div>
                        <div className={styles.chartAxis}>
                            <span>Now</span>
                            <span>Retirement ({result.retirementAge})</span>
                            <span>Age 95</span>
                        </div>
                        <div className={styles.chartLegend}>
                            <div className={styles.legendItem}>
                                <div className={`${styles.legendColor} ${styles.normal}`} />
                                <span>Normal</span>
                            </div>
                            <div className={styles.legendItem}>
                                <div className={`${styles.legendColor} ${styles.retirement}`} />
                                <span>Retirement</span>
                            </div>
                            <div className={styles.legendItem}>
                                <div className={`${styles.legendColor} ${styles.shortfall}`} />
                                <span>Shortfall</span>
                            </div>
                            <div className={styles.legendItem}>
                                <span className={styles.legendIcon}>★</span>
                                <span>Goal Event</span>
                            </div>
                        </div>
                    </div>

                    {result.goalFundingSummary && result.goalFundingSummary.length > 0 && (
                        <div className={styles.goalsSection}>
                            <h3 className={styles.goalsSectionTitle}>Goal Outcomes</h3>
                            <div className={styles.goalsGrid}>
                                {result.goalFundingSummary.map((g: GoalFundingSummary) => {
                                    const fillClass =
                                        g.fundingRate >= 80 ? '' : g.fundingRate >= 50 ? styles.medium : styles.low;
                                    const isFunded = g.fundingRate >= 80;
                                    return (
                                        <div key={g.goalId} className={styles.goalCard}>
                                            <div className={styles.goalCardHeader}>
                                                <span className={styles.goalLabel}>{g.label}</span>
                                                <span
                                                    className={`${styles.goalStatusBadge} ${isFunded ? styles.funded : styles.atRisk}`}
                                                >
                                                    {isFunded ? 'On Track' : 'At Risk'}
                                                </span>
                                            </div>
                                            <p className={styles.goalMeta}>
                                                {GOAL_CATEGORY_LABELS[g.category]} &bull; Age {g.targetAge} &bull;{' '}
                                                {formatCurrency(g.targetAmount, result.baseCurrency)}
                                            </p>
                                            <div className={styles.goalFundingBar}>
                                                <div
                                                    className={`${styles.goalFundingFill} ${fillClass}`}
                                                    style={{ width: `${g.fundingRate}%` }}
                                                />
                                            </div>
                                            <p className={styles.goalFundingLabel}>
                                                Funded in {g.fundingRate}% of simulations
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {result.withdrawalSummary && result.withdrawalSummary.drawdownSequence.length > 0 && (
                        <div className={styles.drawdownSection}>
                            <h3 className={styles.drawdownTitle}>Recommended Drawdown Strategy</h3>
                            <p className={styles.drawdownSubtitle}>
                                Tax-optimized withdrawal sequence for your retirement residency
                            </p>

                            <div className={styles.sequenceList}>
                                {result.withdrawalSummary.drawdownSequence.map((entry) => (
                                    <div key={entry.accountType} className={styles.sequenceRow}>
                                        <span className={styles.sequenceRank}>{entry.priority}</span>
                                        <span className={styles.sequenceLabel}>{entry.label}</span>
                                        <span
                                            className={`${styles.taxBadge} ${
                                                entry.taxCategory === 'TAX_FREE'
                                                    ? styles.taxFree
                                                    : entry.taxCategory === 'CAPITAL_GAINS'
                                                      ? styles.capitalGains
                                                      : entry.taxCategory === 'TAX_DEFERRED'
                                                        ? styles.taxDeferred
                                                        : styles.highTax
                                            }`}
                                        >
                                            {entry.taxCategory.replace(/_/g, ' ')}
                                        </span>
                                        <span className={styles.sequenceRate}>
                                            {(entry.effectiveTaxRate * 100).toFixed(1)}%
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className={styles.savingsRow}>
                                <div className={styles.savingsMetric}>
                                    <span className={styles.savingsLabel}>Effective Withdrawal Tax</span>
                                    <span className={styles.savingsValue}>
                                        {(result.withdrawalSummary.effectiveTaxRate * 100).toFixed(1)}%
                                    </span>
                                </div>
                                {result.withdrawalSummary.taxSavedVsNaive > 0 && (
                                    <div className={styles.savingsMetric}>
                                        <span className={styles.savingsLabel}>Est. Tax Savings</span>
                                        <span className={`${styles.savingsValue} ${styles.savings}`}>
                                            {formatCurrency(
                                                result.withdrawalSummary.taxSavedVsNaive,
                                                result.baseCurrency,
                                            )}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {showSavePrompt && (
                        <div className={styles.savePrompt}>
                            <div className={styles.savePromptContent}>
                                <h3>Save this scenario?</h3>
                                <input
                                    type="text"
                                    className={styles.scenarioInput}
                                    placeholder="e.g., Retire at 50 in India"
                                    value={scenarioName}
                                    onChange={(e) => setScenarioName(e.target.value)}
                                />
                                <div className={styles.savePromptActions}>
                                    <button className={styles.cancelSave} onClick={() => setShowSavePrompt(false)}>
                                        Skip
                                    </button>
                                    <button
                                        className={styles.saveSave}
                                        onClick={handleSaveScenario}
                                        disabled={!scenarioName.trim()}
                                    >
                                        Save Scenario
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {savedScenarios.length > 0 && (
                <div className={styles.scenariosSection}>
                    <h3 className={styles.scenariosSectionTitle}>Saved Scenarios</h3>
                    <div className={styles.scenariosGrid}>
                        {savedScenarios.map((scenario) => (
                            <div key={scenario.id} className={styles.scenarioCard}>
                                <div className={styles.scenarioHeader}>
                                    <span className={styles.scenarioName}>{scenario.name}</span>
                                    <button
                                        className={styles.scenarioDelete}
                                        onClick={() => {
                                            deleteScenario(scenario.id);
                                            setSavedScenarios(getSavedScenarios());
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div className={styles.scenarioBody}>
                                    <div className={styles.scenarioRow}>
                                        <span>
                                            Age {scenario.inputs.retirementAge} in {scenario.inputs.retirementCountry}
                                        </span>
                                        <span className={styles.scenarioValue}>
                                            {scenario.results.successRate}% success
                                        </span>
                                    </div>
                                    <div className={styles.scenarioRow}>
                                        <span>Safe spend</span>
                                        <span className={styles.scenarioValue}>
                                            {formatCurrency(
                                                scenario.results.safeAnnualSpend,
                                                scenario.results.baseCurrency,
                                            )}
                                            /yr
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
